import type { Plan } from '@/api/subscription-hooks'
import { currencySymbol, jLocale, taxInclusive, taxLabel, taxRatePct } from '@/lib/jurisdiction'

/**
 * How a plan's price is written for a human.
 *
 * Extracted from PlanSettings (CLA-961) because UpgradePanel had grown its own
 * version — `from $${plan.monthly_seat_price}/practitioner/mo` — which reads
 * "from $149/mo" for Clinic when the least a clinic can pay for Clinic is $349.
 * `monthly_seat_price` on a base-price plan is the price of each ADDITIONAL
 * practitioner, never an entry price. Two surfaces quoting money from the same
 * fields is exactly the shape that drifts, so there is now one implementation.
 *
 * The underlying rule (CLA-937): a plan with a base price is charged at the
 * CLINIC level — the base includes the first practitioner, and the seat price
 * applies to each additional one. A null base means a seat-only plan, where the
 * seat price genuinely does apply per practitioner.
 */

function formatAud(value: string | null | undefined): number | null {
  if (value == null) return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

export function basePrice(plan: Plan, interval: 'monthly' | 'annual'): number | null {
  return formatAud(interval === 'monthly' ? plan.monthly_base_price : plan.annual_base_price)
}

export function seatPrice(plan: Plan, interval: 'monthly' | 'annual'): number | null {
  return formatAud(interval === 'monthly' ? plan.monthly_seat_price : plan.annual_seat_price)
}

/**
 * A yearly figure — whole dollars when it is whole, cents when it isn't, and
 * always grouped ("A$1,428", "A$1,570.80"), because a four-digit number with
 * no separator is the hardest kind to read at a glance.
 */
function money(n: number, cents = false): string {
  const whole = !cents && Number.isInteger(Math.round(n * 100) / 100)
  return `${currencySymbol()}${n.toLocaleString(jLocale(), {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  })}`
}

/**
 * The headline amount and which column it came from. A base-price plan is
 * clinic-level, so its headline is the base; a seat-only plan's is the seat.
 */
function headline(plan: Plan, interval: 'monthly' | 'annual') {
  const base = basePrice(plan, interval)
  return { base, ex: base ?? seatPrice(plan, interval) }
}

/**
 * The headline price. A plan with a base price is clinic-level, so the headline
 * is the CLINIC price and reads "/mo" — quoting "/seat/mo" there would imply the
 * whole figure repeats per practitioner, which is the misreading the hybrid
 * pricing exists to avoid (CLA-937). Seat-only plans are unchanged.
 *
 * Annual leads with the YEARLY amount (CLA-1622). The catalogue's annual
 * columns are a per-month equivalent, but Stripe's annual checkout shows the
 * year — "A$1,428 + GST" — and a clinician told "A$119/seat/mo billed
 * annually" met a total that looked unrelated to what she had been quoted.
 * The monthly equivalent stays alongside so the comparison with Monthly holds.
 */
export function priceLine(plan: Plan, interval: 'monthly' | 'annual'): string | null {
  const { base, ex } = headline(plan, interval)
  if (ex == null || ex === 0) return null

  const sym = currencySymbol()
  if (interval === 'annual') {
    const per = base != null ? '/yr' : '/seat/yr'
    const year = ex * 12
    const monthly = `(${sym}${ex.toFixed(0)}/mo)`
    if (taxInclusive()) {
      const inc = year * (1 + taxRatePct() / 100)
      return `${money(year)}${per} ex-${taxLabel()} ${monthly} · ${money(inc, true)} inc ${taxLabel()}`
    }
    return `${money(year)}${per} plus ${taxLabel().toLowerCase()} ${monthly}`
  }

  const per = base != null ? '/mo' : '/seat/mo'
  if (taxInclusive()) {
    // Tax-inclusive jurisdictions (AU): quote ex-tax with the inc-tax figure alongside.
    const inc = ex * (1 + taxRatePct() / 100)
    return `${sym}${ex.toFixed(0)}${per} ex-${taxLabel()} (${sym}${inc.toFixed(2)} inc ${taxLabel()})`
  }
  // Tax-exclusive jurisdictions (US): tax is computed at checkout.
  return `${sym}${ex.toFixed(0)}${per} plus ${taxLabel().toLowerCase()}`
}

/**
 * The short form, for places too tight for the tax parenthetical (the upgrade
 * panel's button). Same base-vs-seat rule — never quotes a seat price as if it
 * were the entry price of a clinic-level plan. Annual leads with the year, as
 * `priceLine` does, so the button matches what Stripe then shows.
 */
export function shortPriceLine(plan: Plan, interval: 'monthly' | 'annual'): string | null {
  const { base, ex } = headline(plan, interval)
  if (ex == null || ex === 0) return null
  const sym = currencySymbol()
  if (interval === 'annual') {
    return `${money(ex * 12)}${base != null ? '/yr' : '/practitioner/yr'} (${sym}${ex.toFixed(0)}/mo)`
  }
  return `${sym}${ex.toFixed(0)}${base != null ? '/mo' : '/practitioner/mo'}`
}

/**
 * What a year on Annual saves over a year on Monthly, for the plan's headline
 * price — the base on a base-price plan, the seat on a seat-only one, the same
 * figure `priceLine` leads with (CLA-1622). Computed from the catalogue, never
 * written down, so a price change can't leave a stale promise behind.
 *
 * Null when there is no saving to state: unpriced, a headline that changes
 * kind between intervals (comparing a base with a seat would be meaningless),
 * or an annual price that isn't actually cheaper.
 */
export function annualSaving(plan: Plan): number | null {
  const m = headline(plan, 'monthly')
  const a = headline(plan, 'annual')
  if (m.ex == null || a.ex == null) return null
  if ((m.base == null) !== (a.base == null)) return null
  const saving = (m.ex - a.ex) * 12
  return saving > 0 ? saving : null
}

/**
 * The Annual option's label: "Annual · save A$360/yr" when every priced plan
 * on offer saves, otherwise plain "Annual". With several plans that save
 * different amounts it quotes the smallest as "from", which is true of every
 * one of them — never the largest, which would overpromise the cheaper plan.
 */
export function annualOptionLabel(plans: Plan[]): string {
  const priced = plans.filter((p) => headline(p, 'monthly').ex)
  if (priced.length === 0) return 'Annual'
  const savings = priced.map(annualSaving)
  if (savings.some((s) => s == null)) return 'Annual'
  const amounts = savings as number[]
  const least = Math.min(...amounts)
  const from = amounts.some((s) => s !== least) ? 'from ' : ''
  return `Annual · save ${from}${money(least)}/yr`
}

/**
 * What the base covers, and what each extra practitioner costs. Only rendered
 * for base-price plans — on a seat-only plan the headline already says
 * "/seat/mo" and a second line would just repeat it.
 */
export function seatNote(plan: Plan, interval: 'monthly' | 'annual'): string | null {
  if (basePrice(plan, interval) == null) return null

  const seat = seatPrice(plan, interval)
  if (seat == null || seat === 0) return null

  if (interval === 'annual') {
    return `Includes your first practitioner, then ${money(seat * 12)}/yr (${currencySymbol()}${seat.toFixed(0)}/mo) each additional`
  }
  return `Includes your first practitioner, then ${currencySymbol()}${seat.toFixed(0)}/mo each additional`
}

/**
 * How many of a clinic's members are actually billed.
 *
 * `billable_seats/1` on the server counts `role = 'practitioner'` and nothing
 * else — admin, staff and viewer accounts are free on every tier, and they
 * genuinely cannot write notes, so the line sits where the value is. Counted
 * from the members list the Staff screen already holds rather than fetched,
 * because a second source for the same number is how the two disagree.
 *
 * ⚠ A practitioner who is ALSO a clinic admin is still billed. `is_admin` is
 * a separate boolean that does not affect billing, and most owners practise.
 */
export function billablePractitioners(members: { roles?: string[] }[]): number {
  return members.filter(m => (m.roles ?? []).includes('practitioner')).length
}

/**
 * The clinic's monthly total for `practitioners`, ex-tax.
 *
 * Same base-vs-seat rule as `priceLine`: a base price INCLUDES the first
 * practitioner and the seat price applies to each additional one. Quoting
 * base + (seat × N) would over-charge every clinic by exactly one seat.
 *
 * Null when the plan has no price to quote (early access, free, network) —
 * callers say something else rather than render "$0", which reads as a promise.
 */
export function monthlyTotalFor(
  plan: Plan,
  interval: 'monthly' | 'annual',
  practitioners: number,
): string | null {
  const seat = seatPrice(plan, interval)
  if (seat == null || seat === 0) return null

  const billed = Math.max(practitioners, 0)
  const base = basePrice(plan, interval)
  const total = base == null ? seat * billed : base + seat * Math.max(billed - 1, 0)

  return `${currencySymbol()}${total.toFixed(0)}`
}

/**
 * What adding one more member of `role` costs per month, or null when there
 * is nothing to say — an unbilled role, a plan with no price, or the first
 * practitioner on a base-priced plan, who is already covered by the base.
 *
 * Returning null rather than "free" is deliberate: the caller decides the
 * wording, and "free" is a stronger claim than "this particular addition
 * costs nothing extra right now".
 */
export function costOfAddingOne(
  plan: Plan,
  interval: 'monthly' | 'annual',
  role: string,
  currentPractitioners: number,
): string | null {
  if (role !== 'practitioner') return null

  const seat = seatPrice(plan, interval)
  if (seat == null || seat === 0) return null

  const base = basePrice(plan, interval)
  if (base != null && currentPractitioners === 0) return null

  return `${currencySymbol()}${seat.toFixed(0)}`
}
